// src/pages/AccountDirectoryPage.tsx
//
// ★★ ADR 0011 — the first screen over Annex A stories 52–59, which had NONE.
//
// ══ WHY THIS IS A READ SURFACE AND NOT "THE CAM SCREEN" ══
//
// `DEMO-READINESS-PLAN.md`'s Wave 2a asked for a CAM screen with user
// disable/enable, a leaver date and membership editing. Every route it named is
// gated by `requireCamAgent` — a shared MACHINE credential — and `cam-auth.ts`
// gives the reason as a security argument: a PSD officer's own cookie must not be
// sufficient to reach them, because that would make "act as CAM" a capability of
// anyone who can steal or coerce a PSD session. **CAM is a machine counterparty
// that calls US** (P2C3 §1.1.1(b)); a screen is the wrong shape for it.
//
// So this screen reads a PSD-only surface (`/api/administration/accounts*`) and
// carries NO write control at all. ADR 0011 records the refusal and what would
// have to be answered first: which system is the system of record when CAM and
// PSD disagree about an account.
//
// ══ THE THREE SENTENCES THIS SCREEN MUST NOT DROP ══
//
// A read surface misleads by omission, and each of these comes from the SERVER
// rather than being written here — so the screen cannot get out of step with what
// the routes actually allow:
//
//   * **`writeAuthorityNote`** — PSD cannot change accounts here, and CAM does.
//     Absent buttons otherwise read as an unfinished screen.
//   * **`membershipNote`** — a CAM group confers NO `app.user_scope` grant
//     (A-46, Q-35 unanswered). Wave 2a named this: a group list beside a product
//     where grants decide everything is exactly where a customer infers a
//     permission model that does not exist.
//   * **`integrationNote`** — the CAM integration is a shared header credential
//     today; real registration and mutual TLS are Bucket B.
import { plainProse } from '../lib/plain-words'
import { CategoryBadge, RoleBadge } from '../components/StatusBadge'
import { useEffect, useState, type FormEvent } from 'react'
import { SgdsAlert, SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { usePager } from '../lib/usePager'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'

/**
 * ★★★ ADDED 2026-08-24 BY THE SORTABLE-HEADER SWEEP — eight plain headers across
 * this screen's two tables, and both lists are KEYSET-PAGED.
 *
 * ⚠ Sorted on the SERVER, and here it took a cursor change to do it honestly: a
 * cursor is a POSITION IN ONE SPECIFIC ORDER, so it now names the sort it was minted
 * under and the server refuses one minted under a different one. See
 * `decodeCamCursor` in `services/administration/src/cam-directory-repository.ts`.
 */
type AccountSortKey = 'email' | 'displayName' | 'role' | 'agencyCode'

const ACCOUNT_COLUMN_LABELS: Readonly<Record<AccountSortKey, string>> = {
  email: 'Email',
  displayName: 'Name',
  role: 'Role',
  agencyCode: 'Agency',
}

type GroupSortKey = 'name'

const GROUP_COLUMN_LABELS: Readonly<Record<GroupSortKey, string>> = {
  name: 'Group',
}


export function AccountDirectoryPage() {
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const pager = usePager()

  // A new search is a different population, so the keyset walk restarts.
  useEffect(() => pager.reset(), [search])

  const accountSort = useSort<AccountSortKey>()
  const accounts = useApi(
    () =>
      api.accountDirectory({
        ...(search.length > 0 && { search }),
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
        ...sortParams(accountSort.sort),
      }),
    [search, pager.cursor, accountSort.sort.column, accountSort.sort.dir],
  )

  /*
    ⚠★ A SORT CHANGE MUST RESET THE KEYSET WALK, and on this screen that is not
    merely tidy — it is what keeps the walk correct.

    A cursor is a position in ONE order. The server now refuses a cursor minted under
    a different sort (`decodeCamCursor`), so without this reset a header click on
    page three would silently restart at page one while the pager still said three:
    correct rows, a lying control. Resetting the pager makes the two agree.
  */
  const onAccountSort = (column: AccountSortKey) => {
    accountSort.onSort(column)
    pager.reset()
  }

  const groupSort = useSort<GroupSortKey>()
  const groups = useApi(
    () => api.accountGroups(sortParams(groupSort.sort)),
    [groupSort.sort.column, groupSort.sort.dir],
  )

  const rows = accounts.data?.accounts ?? []
  const total = accounts.data?.total ?? 0
  const nextCursor = accounts.data?.nextCursor ?? null

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Accounts and groups</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          The accounts this system holds and the CAM groups they belong to.
          PSD-only, and <strong>read-only</strong>.
        </p>
      </div>

      {/*
        ⚠ The write-authority sentence comes from the SERVER's own response, so a
        change to what the routes permit cannot leave this screen claiming the
        old rule. It is an `info` alert rather than a warning: nothing is wrong
        — this is how the product is meant to work.
      */}
      {accounts.data?.writeAuthorityNote !== undefined && (
        <SgdsAlert show variant="info" title="Account changes are made by CAM, not here">
          {plainProse(accounts.data.writeAuthorityNote)}
        </SgdsAlert>
      )}

      <div className="pt-card pt-no-print">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            setSearch(searchDraft.trim())
          }}
        >
          <label className="flex flex-col gap-1 text-sm flex-1 min-w-[16rem] max-w-md">
            <span className="font-semibold">Search by email or name</span>
            <input
              type="text"
              className="pt-native-date"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Part of an email address or a display name"
            />
          </label>
          <SgdsButton type="submit" variant="primary">
            Search
          </SgdsButton>
          {search.length > 0 && (
            <SgdsButton
              type="button"
              variant="outline"
              onClick={() => {
                setSearchDraft('')
                setSearch('')
              }}
            >
              Clear search
            </SgdsButton>
          )}
        </form>
      </div>

      {accounts.error !== null ? (
        <ErrorCard title="Could not load accounts" message={accounts.error} onRetry={accounts.reload} />
      ) : accounts.initialLoading ? (
        <LoadingCard label="Loading accounts" rows={5} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No accounts match"
          message={
            search.length > 0
              ? 'Nothing matches that search. Clear it to see the whole directory.'
              : 'This directory is readable by PSD only, and it is empty, which on this surface is unusual enough to be worth checking the database was seeded.'
          }
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <SortAnnouncement
            sort={accountSort.sort}
            interacted={accountSort.interacted}
            busy={accounts.loading}
            columnLabels={ACCOUNT_COLUMN_LABELS}
            rowCount={rows.length}
          />
          <div className="pt-table-wrap" aria-busy={accounts.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Accounts in the CAM directory{search.length > 0 ? ` matching "${search}"` : ''}, one page at a time
              </caption>
              <thead>
                <tr>
                  <SortableTh label={ACCOUNT_COLUMN_LABELS.email} sortKey="email" sort={accountSort.sort} onSort={onAccountSort} />
                  <SortableTh label={ACCOUNT_COLUMN_LABELS.displayName} sortKey="displayName" sort={accountSort.sort} onSort={onAccountSort} />
                  <SortableTh label={ACCOUNT_COLUMN_LABELS.role} sortKey="role" sort={accountSort.sort} onSort={onAccountSort} />
                  <SortableTh label={ACCOUNT_COLUMN_LABELS.agencyCode} sortKey="agencyCode" sort={accountSort.sort} onSort={onAccountSort} />
                  {/*
                    ⚠★ THE ONE COLUMN ON THIS TABLE THAT DOES NOT SORT, and it is a
                    paging argument rather than a judgement about whether anybody
                    would want it.

                    ACTIVE / DISABLED is two values over thousands of rows. A keyset
                    walk needs `(sort_column, id)` to make progress, and with two
                    distinct leading values the predicate degenerates: every page
                    after the first re-scans one of two enormous partitions. It is
                    also the wrong SHAPE of control — an officer looking for the
                    disabled accounts wants to see only those, which is a filter, and
                    a sort would leave them counting down a page instead.

                    ⚠ Absent from `CAM_USER_SORT_COLUMNS` too, so the refusal is at
                    the SERVER and not only here.
                  */}
                  <th scope="col" data-unsortable="a two-valued column — a keyset walk over (is_active, id) has exactly two leading values, so every page after the first re-scans one of two enormous partitions; a FILTER is the right control here, not a sort">
                    Account
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => (
                  <tr key={account.id}>
                    <td className="pt-mono">{account.email}</td>
                    <td>{account.displayName}</td>
                    <td><RoleBadge role={account.role} /></td>
                    <td>{account.agencyCode}</td>
                    <td>
                      {/*
                        ⚠ ACTIVE / DISABLED is the one piece of account state a
                        PSD officer most needs to see and cannot change here —
                        which is exactly why it is rendered as plain emphasised
                        TEXT rather than as anything resembling a toggle.
                        ⚠ And deliberately NOT `BillStatusBadge`: that component
                        maps a BILL's lifecycle, and borrowing its vocabulary for
                        an account would put "APPROVED" on a person.
                      */}
                      <CategoryBadge kind="state" value={account.isActive ? 'ACTIVE' : 'DISABLED'} label={account.isActive ? 'Active' : 'Disabled'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[var(--sgds-border-color-muted)]">
            <p className="text-sm text-[var(--sgds-color-subtle)] m-0">
              Showing {rows.length} of {total} account{total === 1 ? '' : 's'}.
            </p>
            <div className="flex items-center gap-2 shrink-0 pt-no-print">
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={!pager.canGoPrevious || accounts.loading}
                onClick={pager.goPrevious}
              >
                Previous
              </SgdsButton>
              <SgdsButton
                variant="outline"
                size="sm"
                type="button"
                disabled={nextCursor === null || accounts.loading}
                onClick={() => {
                  if (nextCursor !== null) pager.goNext(nextCursor)
                }}
              >
                Next
              </SgdsButton>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <h2 className="text-xl">CAM groups</h2>

        {/*
          ★★ THE SENTENCE WAVE 2a ASKED FOR BY NAME, and it is a `warning`
          rather than `info` because the inference it prevents is a wrong belief
          about who can see what.
        */}
        {groups.data?.membershipNote !== undefined && (
          <SgdsAlert show variant="warning" title="A group confers no permission in PRS2">
            {plainProse(groups.data.membershipNote)}
          </SgdsAlert>
        )}

        {groups.error !== null ? (
          <ErrorCard title="Could not load groups" message={groups.error} onRetry={groups.reload} />
        ) : groups.initialLoading ? (
          <LoadingCard label="Loading groups" rows={3} />
        ) : (groups.data?.groups ?? []).length === 0 ? (
          <EmptyCard
            title="No groups recorded"
            message="No CAM group exists in this deployment. Groups are created outside PRS2.0, in the government account service, so this product never creates one."
          />
        ) : (
          <div className="pt-card p-0 overflow-hidden">
            <SortAnnouncement
              sort={groupSort.sort}
              interacted={groupSort.interacted}
              busy={groups.loading}
              columnLabels={GROUP_COLUMN_LABELS}
              rowCount={(groups.data?.groups ?? []).length}
            />
            <div className="pt-table-wrap" aria-busy={groups.loading}>
              <table className="pt-table">
                <caption className="sr-only">
                  CAM groups recorded in this deployment, with each group's member count
                </caption>
                <thead>
                  <tr>
                    <SortableTh label={GROUP_COLUMN_LABELS.name} sortKey="name" sort={groupSort.sort} onSort={groupSort.onSort} />
                    <th scope="col" data-unsortable="free text that can be blank, and a blank value has no place in an order: sorting on it would silently drop the groups with no description">
                      Description
                    </th>
                    <th scope="col" data-unsortable="a count of a nested membership list, and the list is resolved per group rather than aggregated in the query the keyset walk orders — so there is no column for the server to sort by and a client sort would order one page">
                      Members
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(groups.data?.groups ?? []).map((group) => (
                    <tr key={group.id}>
                      <td className="font-semibold">{group.name}</td>
                      <td className="text-sm">{group.description ?? 'None'}</td>
                      <td className="pt-tabular">
                        {group.memberUserIds.length}
                        <span className="sr-only"> member{group.memberUserIds.length === 1 ? '' : 's'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {accounts.data?.integrationNote !== undefined && (
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          {plainProse(accounts.data.integrationNote)}
        </p>
      )}
    </div>
  )
}
